import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { UploadCloud, Check, X } from "lucide-react";
import { api } from "@/lib/api";

interface Props {
  label?: string;
  folder?: string;
  accept?: string;
  onUploaded: (url: string) => void;
  value?: string | null;
  /** Text of the upload button; defaults to the generic "Subir archivo". */
  buttonLabel?: string;
}

export function FileUpload({ label, folder = "costadorada1", accept = ".pdf,.docx,.jpg,.jpeg,.png,.heic,.webp", onUploaded, value, buttonLabel }: Props) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleFile(file: File) {
    setError(null);
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/upload?folder=${folder}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded(data.url);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? "Error al subir");
    } finally {
      setUploading(false);
    }
  }

  return (
    <div>
      {label && <label className="label">{label}</label>}
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept={accept}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleFile(f);
          }}
        />
        <button
          type="button"
          className="btn-secondary"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
        >
          {uploading ? (
            <>{t("legal.uploading")}</>
          ) : value ? (
            <><Check size={14} className="text-green-600" /> {buttonLabel ?? t("legal.uploadFile")}</>
          ) : (
            <><UploadCloud size={14} /> {buttonLabel ?? t("legal.uploadFile")}</>
          )}
        </button>
        {value && (
          <a href={value} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline truncate flex-1">
            {value}
          </a>
        )}
        {value && (
          <button type="button" className="btn-ghost" onClick={() => onUploaded("")}>
            <X size={14} />
          </button>
        )}
      </div>
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
}
